import React, {useState, useEffect} from 'react';
import {Box, Button, LinearProgress, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useLazyQuery} from '@apollo/client';
import {Delete} from '@mui/icons-material';
import {
  ActionType,
  SelectAutocompleteField,
  AutoField,
} from '../../components/AutoForm/AutoFieldNew';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import {useReportPack} from './useReportPack';
import {reportPackQuery} from './reportPackQueries';
import {
  ReportPackQuery,
  ReportPackQueryVariables,
} from '../../schema-types/ReportPackQuery';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {ExistingFile} from './ReportPackExistingFile';
import {getReportPackDisabled} from './ReportPackHelpers';
import {ReportPackStatus} from '../../schema-types/globalTypes';

interface ReportPackGenerateSmartflowProps {
  report: ReportPackFragment;
}

interface SmartflowFormValues {
  selectedSmartflowId: string | null;
}

const ReportPackGenerateSmartflowInner: React.FC<ReportPackGenerateSmartflowProps> = ({
  report,
}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {reviewId} = useReviewContext();
  const {
    onUploadSmartflowToReportPack,
    loadingUploadSmartflow,
    onDeleteReportPackSmartflowExecution,
    loadingDeleteSmartflowExecution,
    uploadSmartflowData,
    uploadSmartflowError,
  } = useReportPack();
  const [isPolling, setIsPolling] = useState(false);
  const [executionStatus, setExecutionStatus] = useState<string | null>(null);

  // Use the Formik context to access form values and submission
  const {values} = useAutoForm<SmartflowFormValues>();

  // Polling query for smartflow execution status
  const [startPolling, {data: pollingReportPack, stopPolling}] = useLazyQuery<
    ReportPackQuery,
    ReportPackQueryVariables
  >(reportPackQuery, {
    variables: {reviewId},
    pollInterval: 5000, // Poll every 5 seconds
    fetchPolicy: 'network-only',
  });

  // Create options for the select field from available smartflow templates
  const smartflowOptions =
    report.availableSmartflowTemplates?.map((template) => ({
      label: template.name || 'Unknown Template',
      value: template.id,
    })) || [];

  // Define the form field
  const selectDocumentField: SelectAutocompleteField<SmartflowFormValues> = {
    id: 'selectedSmartflowId',
    type: ActionType.selectAutocomplete,
    label: 'Select document',
    options: smartflowOptions,
    fullWidth: true,
    canBeEmpty: false,
  };

  // Handle form submission
  const handleCreateDocument = async () => {
    if (!values.selectedSmartflowId) {
      enqueueSnackbar('Please select a document to generate', {variant: 'error'});
      return;
    }
    closeSnackbar();
    try {
      const result = await onUploadSmartflowToReportPack({
        variables: {
          smartflowId: values.selectedSmartflowId,
          reportPackId: report.id,
        },
      });

      if (result.data?.uploadSmartflowToReportPack?.ok) {
        enqueueSnackbar('Document generation started successfully', {variant: 'success'});
        setIsPolling(true);
        setExecutionStatus('started');
        startPolling();
      } else {
        const errors = result.data?.uploadSmartflowToReportPack?.errors;
        enqueueSnackbar(
          `Failed to start document generation: ${errors?.join(', ') || 'Unknown error'}`,
          {
            variant: 'error',
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(`Error starting document generation: ${error}`, {variant: 'error'});
    }
  };

  // Handle delete execution
  const handleDeleteExecution = async () => {
    try {
      const result = await onDeleteReportPackSmartflowExecution({
        variables: {
          reportPackId: report.id,
        },
        refetchQueries: [
          {
            query: reportPackQuery,
            variables: {reviewId},
          },
        ],
        awaitRefetchQueries: true,
      });

      if (result.data?.deleteReportPackSmartflowExecution?.ok) {
        enqueueSnackbar(
          'Warning: SmartFlow Document is still in SharePoint. Please can you manually delete from SharePoint',
          {
            variant: 'error',
            persist: true,
          }
        );
        setIsPolling(false);
        setExecutionStatus(null);
        stopPolling();
      } else {
        const errors = result.data?.deleteReportPackSmartflowExecution?.errors;
        enqueueSnackbar(
          `Failed to remove document: ${errors?.join(', ') || 'Unknown error'}`,
          {
            variant: 'error',
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(`Error removing document: ${error}`, {
        variant: 'error',
      });
    }
  };

  // Start polling when the component mounts
  useEffect(() => {
    if (
      !isPolling &&
      report.smartflowExecution &&
      report.smartflowExecution.status !== 'complete'
    ) {
      setIsPolling(true);
      startPolling();
    }
  }, []); // no dependencies, run once on mount

  // Monitor polling data for status changes
  useEffect(() => {
    if (isPolling && pollingReportPack?.getReportPackByReview?.smartflowExecution) {
      const execution = pollingReportPack.getReportPackByReview.smartflowExecution;
      setExecutionStatus(execution.status || 'unknown');

      if (execution.isComplete) {
        setIsPolling(false);
        stopPolling();
        enqueueSnackbar('Document generation completed', {
          variant: 'success',
          persist: true,
        });
      } else if (execution.status === 'failed' || execution.status === 'error') {
        setIsPolling(false);
        stopPolling();
        enqueueSnackbar('Document generation failed', {variant: 'error', persist: true});
      }
    }
  }, [pollingReportPack, isPolling, stopPolling, enqueueSnackbar]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Check if document generation is in progress
  const isGenerating =
    loadingUploadSmartflow || isPolling || loadingDeleteSmartflowExecution;

  // Check if there's already a smartflow execution (any non-complete execution)
  const hasExistingExecution =
    report.smartflowExecution && !report.smartflowExecution.isExecuted;

  const disableFormDeletion =
    loadingDeleteSmartflowExecution ||
    getReportPackDisabled(report) ||
    !report.isEditable ||
    report.status === ReportPackStatus.PARTLY_READ ||
    report.status === ReportPackStatus.PARTLY_SIGNED ||
    report.status === ReportPackStatus.SUBMITTED;

  return (
    <Box>
      <ExistingFile reportPack={report} />
      {smartflowOptions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No smartflow templates available for this report pack.
        </Typography>
      ) : (
        <>
          <Box sx={{mb: 3}}>
            <AutoField field={selectDocumentField} />
          </Box>

          <Button
            variant="contained"
            color="primary"
            disabled={
              isGenerating ||
              (Boolean(hasExistingExecution) &&
                report.smartflowExecution?.status !== 'failed')
            }
            onClick={handleCreateDocument}
            fullWidth
            sx={{mb: 2}}
          >
            {isGenerating ? 'Generating...' : 'Create Document'}
          </Button>

          {isGenerating && (
            <Box sx={{mb: 2}}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Document generation in progress...
              </Typography>
              <LinearProgress />
              {executionStatus && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                  Status: {executionStatus}
                </Typography>
              )}
            </Box>
          )}

          {report.smartflowExecution?.status === 'failed' ? (
            <Box sx={{mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1}}>
              <Typography variant="body2" color="white">
                Document generation failed. Please try again or contact support.
              </Typography>
            </Box>
          ) : null}

          {uploadSmartflowError?.message ? (
            <Box sx={{mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1}}>
              <Typography variant="body2" color="white">
                Document generation failed. Please try again or contact support.
              </Typography>
            </Box>
          ) : null}
          {/* {report.smartflowExecution?.status === 'not executed' ? (
            <Box sx={{mb: 2, p: 2, borderRadius: 1, bgcolor: themeColors.lightPurple}}>
              <Typography variant="body2">
                Document generation not yet executed.
              </Typography>
            </Box>
          ) : null} */}

          {report.smartflowExecution?.isComplete && (
            <Box sx={{p: 2, bgcolor: 'success.light', borderRadius: 1}}>
              <Typography variant="body2">
                Document generation completed successfully!
              </Typography>
            </Box>
          )}
          {(isGenerating || hasExistingExecution || report.smartflowExecution?.id) && (
            <Button
              variant="outlined"
              color="error"
              disabled={disableFormDeletion}
              onClick={disableFormDeletion ? () => {} : handleDeleteExecution}
              sx={{mb: 2, mt: 2}}
            >
              <Delete sx={{mr: 1}} />
              Delete document from OneView
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

const ReportPackGenerateSmartflowWrapped = withAutoFormNew<
  SmartflowFormValues,
  ReportPackGenerateSmartflowProps
>(ReportPackGenerateSmartflowInner);

export const ReportPackGenerateSmartflow: React.FC<ReportPackGenerateSmartflowProps> = (
  props
) => {
  return (
    <ReportPackGenerateSmartflowWrapped
      {...props}
      formFields={[]}
      initialValueOverrides={{
        selectedSmartflowId: null,
      }}
    />
  );
};
